import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {Warning} from '@mui/icons-material';

import {getGlobalRoutingConfig, updateGlobalRoute, enableForceGlobal, disableForceGlobal, listOperators} from '../../actions/manageOperators';

// Routing is per CONCRETE message type — no 'any'. Each type has its own global
// default SMSC and its own force-global override (force-global beats tenant + global).
const MESSAGE_TYPES = ['otp', 'transactional', 'promotional'];
const LABEL = {otp: 'OTP', transactional: 'Transactional', promotional: 'Promotional'};

class GlobalOverride extends Component {
  constructor() {
    super();
    this.state = {
      loading: true,
      configError: null,
      operators: [],
      globalRoutes: {},       // message_type -> smsc_id (current default)
      forceGlobal: {},        // message_type -> {enabled, smsc_id}
      selectedGlobal: {},     // message_type -> smsc_id (dropdown)
      selectedForce: {},      // message_type -> smsc_id (dropdown)
      showWait: false
    };
    this.configReq = true;
    this.operatorsReq = true;
    this.actionReq = false;
  }

  componentDidMount() {
    this.props.getGlobalRoutingConfig();
    this.props.listOperators();
  }

  componentWillReceiveProps(nextProps) {
    if (this.configReq && nextProps.globalRoutingConfig) {
      this.configReq = false;
      const res = nextProps.globalRoutingConfig;
      if (res.success) {
        const d = res.data || res;
        const globalRoutes = {};
        (d.global_routes || d.globalRoutes || []).forEach(r => {
          const mt = r.message_type || r.messageType;
          // first entry per type is the default (is_primary / lowest priority)
          if (mt && !globalRoutes[mt]) globalRoutes[mt] = r.smsc_id || r.smscId;
        });
        const forceGlobal = {};
        (d.force_global_routes || d.forceGlobalRoutes || []).forEach(r => {
          const mt = r.message_type || r.messageType;
          if (mt) forceGlobal[mt] = {enabled: !!r.enabled, smsc_id: r.smsc_id || r.smscId || ''};
        });
        this.setState({
          configError: null,
          globalRoutes,
          forceGlobal,
          selectedGlobal: {...globalRoutes},
          selectedForce: MESSAGE_TYPES.reduce((acc, mt) => { acc[mt] = (forceGlobal[mt] && forceGlobal[mt].smsc_id) || ''; return acc; }, {})
        });
      } else {
        this.setState({configError: res.message || res.error || 'Failed to load global routing config'});
      }
      this.checkLoading();
    }
    if (this.operatorsReq && nextProps.operatorsList?.success) {
      this.operatorsReq = false;
      this.setState({operators: nextProps.operatorsList.data?.operators || []});
      this.checkLoading();
    }
    if (this.actionReq && (nextProps.updateGlobalRouteRes || nextProps.enableForceGlobalRes || nextProps.disableForceGlobalRes)) {
      this.actionReq = false;
      this.configReq = true;
      this.props.getGlobalRoutingConfig();
      this.setState({showWait: false});
    }
  }

  checkLoading() {
    if (!this.configReq && !this.operatorsReq) this.setState({loading: false});
  }

  activeOperators() {
    return this.state.operators.filter(op => op.is_active || op.isActive);
  }

  handleUpdateGlobal = (mt) => {
    const smscId = this.state.selectedGlobal[mt];
    if (!smscId) return;
    this.actionReq = true;
    this.setState({showWait: true});
    this.props.updateGlobalRoute(smscId, mt);
  };

  handleEnableForce = (mt) => {
    const smscId = this.state.selectedForce[mt];
    if (!smscId) return;
    this.actionReq = true;
    this.setState({showWait: true});
    this.props.enableForceGlobal(mt, smscId);
  };

  handleDisableForce = (mt) => {
    this.actionReq = true;
    this.setState({showWait: true});
    this.props.disableForceGlobal(mt);
  };

  operatorSelect(value, onChange) {
    return (
      <Select value={value || ''} onChange={onChange} displayEmpty size="small" sx={{minWidth: 240}}>
        <MenuItem value="" disabled>Select SMSC…</MenuItem>
        {this.activeOperators().map(op => (
          <MenuItem key={op.id} value={op.smsc_id || op.smscId}>
            {op.name} ({op.smsc_id || op.smscId})
          </MenuItem>
        ))}
      </Select>
    );
  }

  render() {
    const {loading, configError, globalRoutes, forceGlobal, selectedGlobal, selectedForce} = this.state;
    if (loading) return <div style={{display: 'flex', justifyContent: 'center', padding: 40}}><CircularProgress /></div>;
    if (configError) {
      return (
        <Alert severity="error" sx={{mb: 3}}>
          <Typography variant="subtitle2" sx={{fontWeight: 600}}>Failed to load global routing config</Typography>
          <Typography variant="body2">{configError}</Typography>
          <Button size="small" sx={{mt: 1}} onClick={() => { this.configReq = true; this.setState({loading: true, configError: null}); this.props.getGlobalRoutingConfig(); }}>Retry</Button>
        </Alert>
      );
    }

    const anyForce = MESSAGE_TYPES.some(mt => forceGlobal[mt]?.enabled);

    return (
      <div>
        {anyForce &&
          <Alert severity="warning" sx={{mb: 3}} icon={<Warning />}>
            <Typography variant="subtitle2" sx={{fontWeight: 600}}>Force-Global Override is ACTIVE for some message types</Typography>
            <Typography variant="body2">
              {MESSAGE_TYPES.filter(mt => forceGlobal[mt]?.enabled).map(mt => `${LABEL[mt]} → ${forceGlobal[mt].smsc_id}`).join(', ')}.
              Tenant + global routing is bypassed for those types.
            </Typography>
          </Alert>
        }

        {/* Per-type Global Default SMSC */}
        <Paper variant="outlined" sx={{p: 3, mb: 3, borderRadius: 2}}>
          <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1}}>Global Default SMSC (per message type)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
            Used when a tenant has no route for that message type. If multiple SMSCs exist for a
            type, this is the default (lowest priority).
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Message Type</TableCell><TableCell>Current Default</TableCell><TableCell>Set SMSC</TableCell><TableCell></TableCell></TableRow>
            </TableHead>
            <TableBody>
              {MESSAGE_TYPES.map(mt => (
                <TableRow key={mt}>
                  <TableCell><Chip label={LABEL[mt]} size="small" /></TableCell>
                  <TableCell>{globalRoutes[mt] ? <Chip label={globalRoutes[mt]} color="primary" size="small" /> : <Typography variant="caption" color="text.secondary">none</Typography>}</TableCell>
                  <TableCell>{this.operatorSelect(selectedGlobal[mt], e => this.setState({selectedGlobal: {...selectedGlobal, [mt]: e.target.value}}))}</TableCell>
                  <TableCell>
                    <Button variant="contained" size="small"
                      disabled={!selectedGlobal[mt] || selectedGlobal[mt] === globalRoutes[mt]}
                      onClick={() => this.handleUpdateGlobal(mt)}>Update</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        {/* Per-type Force-Global Override */}
        <Paper variant="outlined" sx={{p: 3, borderRadius: 2, borderColor: anyForce ? 'warning.main' : 'divider'}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
            <Warning color={anyForce ? 'warning' : 'disabled'} />
            <Typography variant="subtitle1" sx={{fontWeight: 600}}>Force-Global Override (per message type)</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
            When enabled for a type, ALL traffic of that type bypasses tenant + global routing and
            goes to the chosen SMSC. Use for emergency operator switches / maintenance.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Message Type</TableCell><TableCell>Status</TableCell><TableCell>Override SMSC</TableCell><TableCell></TableCell></TableRow>
            </TableHead>
            <TableBody>
              {MESSAGE_TYPES.map(mt => {
                const fg = forceGlobal[mt] || {};
                return (
                  <TableRow key={mt}>
                    <TableCell><Chip label={LABEL[mt]} size="small" /></TableCell>
                    <TableCell><Chip label={fg.enabled ? 'ENABLED' : 'DISABLED'} color={fg.enabled ? 'warning' : 'default'} size="small" /></TableCell>
                    <TableCell>{this.operatorSelect(selectedForce[mt], e => this.setState({selectedForce: {...selectedForce, [mt]: e.target.value}}))}</TableCell>
                    <TableCell>
                      {fg.enabled
                        ? <Button variant="outlined" color="success" size="small" onClick={() => this.handleDisableForce(mt)}>Disable</Button>
                        : <Button variant="contained" color="warning" size="small" disabled={!selectedForce[mt]} onClick={() => this.handleEnableForce(mt)}>Enable</Button>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>

        {this.state.showWait && <Box sx={{mt: 2, display: 'flex', justifyContent: 'center'}}><CircularProgress size={24} /></Box>}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    operatorsList: state.manageOperators.listOperatorsResponse,
    globalRoutingConfig: state.manageOperators.globalRoutingConfigResponse,
    updateGlobalRouteRes: state.manageOperators.updateGlobalRouteResponse,
    enableForceGlobalRes: state.manageOperators.enableForceGlobalResponse,
    disableForceGlobalRes: state.manageOperators.disableForceGlobalResponse
  };
}

export default connect(mapStateToProps, {
  getGlobalRoutingConfig, updateGlobalRoute, enableForceGlobal, disableForceGlobal, listOperators
})(GlobalOverride);
